import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { FileText, ExternalLink, AlertCircle, CheckCircle, Clock, Calendar, ImageIcon, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const MyCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [thumbnails, setThumbnails] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    expiring_soon: 0,
    no_expiry: 0
  });

  useEffect(() => {
    fetchMyCertificates();
  }, [showExpired]);

  const fetchMyCertificates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/my-certificates?include_expired=${showExpired}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch certificates');
      }

      const data = await response.json();
      setCertificates(data.certificates || []);
      setStats(data.stats || stats);
      setLoading(false);

      // Load thumbnails for certificates with file IDs
      const certsWithFiles = (data.certificates || []).filter(cert => cert.google_drive_file_id);
      certsWithFiles.forEach((cert, idx) => {
        setTimeout(() => {
          loadThumbnail(cert.google_drive_file_id);
        }, idx * 300); // Stagger loading
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadThumbnail = async (fileId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/certificate-thumbnail/${fileId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setThumbnails(prev => ({ ...prev, [fileId]: url }));
      }
    } catch (err) {
      console.error('Failed to load thumbnail:', err);
    }
  };

  const handleShareCertificates = async () => {
    if (!shareEmail || !shareEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSharing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/share-certificates`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_email: shareEmail,
            include_expired: showExpired
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to share certificates');
      }

      toast.success(`Certificate summary sent to ${shareEmail}`);
      setIsShareDialogOpen(false);
      setShareEmail('');
    } catch (err) {
      toast.error(err.message || 'Failed to share certificates');
    } finally {
      setSharing(false);
    }
  };

  const getStatusBadge = (cert) => {
    const expiryDate = cert.expiry_date ? new Date(cert.expiry_date) : null;
    const now = new Date();

    if (!expiryDate) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="w-3 h-3 mr-1" />
          No Expiry Set
        </span>
      );
    }

    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    } else if (daysUntilExpiry <= 30) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Expires in {daysUntilExpiry} days
        </span>
      );
    } else if (daysUntilExpiry <= 90) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Expires in {daysUntilExpiry} days
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading your certificates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Safety Certificates</h1>
          <p className="text-gray-600">View all your safety training certificates and their expiry dates</p>
        </div>

      {/* Filter Controls */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show expired certificates</span>
          </label>
          
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing {certificates.length} of {stats.total} certificates
          </div>
          
          <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Mail className="h-4 w-4 mr-2" />
                Share via Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Share Certificate Summary</DialogTitle>
                <DialogDescription>
                  Send a summary of your safety certificates to a customer or colleague
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="share-email">Recipient Email</Label>
                  <Input
                    id="share-email"
                    type="email"
                    placeholder="customer@example.com"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div className="text-sm text-gray-600">
                  This will send a summary of your {showExpired ? 'all' : 'active'} certificates ({certificates.length} certificates)
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsShareDialogOpen(false)}
                  disabled={sharing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleShareCertificates}
                  disabled={sharing}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sharing ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <FileText className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-600">Total Certificates</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.expiring_soon}</p>
              <p className="text-sm text-gray-600">Expiring Soon</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
              <p className="text-sm text-gray-600">Expired</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificates Display */}
      {certificates.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600">No certificates found</p>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((cert, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-start justify-between">
                  <span className="flex-1">{cert.certificate_type || cert.ticket_name || 'Unknown Certificate'}</span>
                  {getStatusBadge(cert)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Thumbnail */}
                {cert.google_drive_file_id && thumbnails[cert.google_drive_file_id] ? (
                  <div className="mb-4 rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={thumbnails[cert.google_drive_file_id]} 
                      alt={cert.certificate_type || 'Certificate'}
                      className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificate-file/${cert.google_drive_file_id}`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                          }
                        } catch (err) {
                          console.error('Error opening certificate:', err);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="mb-4 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-48 flex items-center justify-center">
                    <ImageIcon className="h-12 w-12 text-slate-500" />
                  </div>
                )}

                {/* Details */}
                <div className="space-y-2 text-sm">
                  {(cert.issuing_organization || cert.source) && (
                    <div>
                      <span className="font-medium text-gray-700">Issuer:</span>
                      <p className="text-gray-600">{cert.issuing_organization || (cert.source === 'google_drive_scan' ? 'Google Drive Import' : 'Unknown')}</p>
                    </div>
                  )}
                  
                  {cert.issue_date && (
                    <div>
                      <span className="font-medium text-gray-700">Issue Date:</span>
                      <p className="text-gray-600">{formatDate(cert.issue_date)}</p>
                    </div>
                  )}
                  
                  <div>
                    <span className="font-medium text-gray-700">Expiry Date:</span>
                    <p className={`font-semibold ${
                      cert.expiry_date && new Date(cert.expiry_date) < new Date() ? 'text-red-600' :
                      cert.expiry_date && new Date(cert.expiry_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? 'text-orange-600' :
                      'text-gray-600'
                    }`}>
                      {cert.expiry_date ? formatDate(cert.expiry_date) : 'Not Set'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                {(cert.google_drive_file_id || cert.certificate_file_url || cert.google_drive_link) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={async () => {
                      if (cert.google_drive_file_id) {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificate-file/${cert.google_drive_file_id}`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                          }
                        } catch (err) {
                          console.error('Error opening certificate:', err);
                          toast.error('Failed to open certificate');
                        }
                      } else if (cert.google_drive_link) {
                        window.open(cert.google_drive_link, '_blank');
                      } else {
                        window.open(cert.certificate_file_url, '_blank');
                      }
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Full Certificate
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {certificates.map((cert, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {cert.certificate_type || cert.ticket_name || 'Unknown Certificate'}
                      </h3>
                      {getStatusBadge(cert)}
                    </div>
                    
                    <div className="ml-8 space-y-1">
                      {(cert.issuing_organization || cert.source) && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Issuer:</span> {cert.issuing_organization || (cert.source === 'google_drive_scan' ? 'Google Drive Import' : 'Unknown')}
                        </p>
                      )}
                      
                      {cert.issue_date && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Issue Date:</span> {formatDate(cert.issue_date)}
                        </p>
                      )}
                      
                      {cert.expiry_date && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Expiry Date:</span> {formatDate(cert.expiry_date)}
                        </p>
                      )}
                      
                      {cert.certificate_number && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Certificate #:</span> {cert.certificate_number}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {(cert.google_drive_file_id || cert.certificate_file_url || cert.google_drive_link) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (cert.google_drive_file_id) {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificate-file/${cert.google_drive_file_id}`, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              }
                            });
                            
                            if (!response.ok) {
                              throw new Error('Failed to fetch certificate file');
                            }
                            
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                          } catch (err) {
                            console.error('Error fetching certificate:', err);
                            toast.error('Failed to load certificate file');
                          }
                        } else if (cert.google_drive_link) {
                          window.open(cert.google_drive_link, '_blank');
                        } else {
                          window.open(cert.certificate_file_url, '_blank');
                        }
                      }}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View File
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help Section */}
      <Card className="mt-8 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Need to renew a certificate?</h4>
              <p className="text-sm text-blue-800">
                Contact your supervisor or HR department to schedule training for certificates that are expired or expiring soon.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default MyCertificates;
